'use client';
import { isTrustedOfficerIdentity } from '@/lib/participantUtils';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import { createClient } from '@/lib/supabase/client';
import logger from '@/lib/logger';
import {
  transcriptSegmentRowToMessage,
  type TranscriptSegmentRow,
} from '@/lib/transcripts/realtimeSegment';
import {
  fromPersistedTranscript,
} from '@/lib/transcripts/persistence';
import { isTranscriptNormalizedStorageEnabled } from '@/lib/transcripts/normalizedStorageFeature';
import {
  legacyEventAuthorityMode,
  registerTranscriptRealtimeSubscriptions,
  resolveTranscriptRealtimeMode,
  type LegacyTranscriptRealtimeRow,
  type TranscriptRealtimeMode,
} from '@/lib/transcripts/realtimeSubscriptions';
import { loadTranscriptProjection } from '@/lib/server/transcriptProjection';
import {
  mergeTranscripts,
  reconcilePersistedTranscripts,
  TranscriptionMessage,
} from '@/store/slices/transcriptionSlice';
import { setNotes, NoteItem } from '@/store/slices/notesSlice';
import {
  addParticipantChatMessage,
  ParticipantChatMessage,
} from '@/store/slices/participantChatSlice';
import {
  addOfficerChatMessage,
  OfficerChatMessage,
} from '@/store/slices/officerChatSlice';
import { dropUpstreamErrorMessages } from '@/lib/chat/upstreamError';

/**
 * Supabase Realtime Hook
 *
 * Subscribes to Supabase Realtime changes for a specific call.
 * Enables cross-device sync - when another device updates data,
 * this device receives the update in real-time.
 *
 * Architecture:
 * - Subscribes to postgres_changes events on call-related tables
 * - Filters by call_id (DB UUID) to only receive relevant updates
 * - Dispatches updates to Redux store on receipt
 * - Works alongside the sync hooks (they write, this listens)
 *
 * This complements Redis (same-device cross-tab) with cross-device sync.
 */

interface UseSupabaseRealtimeOptions {
  /** Room call ID (text string) */
  callId: string;
  /** Database call UUID (from calls table) */
  dbCallId: string | null;
  /** Whether realtime is enabled */
  enabled?: boolean;
  /**
   * Skip processing Realtime transcript events.
   * Set to true when this device is actively receiving transcripts from LiveKit
   * text streams AND writing them via useTranscriptSync. This avoids processing
   * redundant echoes of the device's own normalized segment writes.
   */
  skipTranscripts?: boolean;
  /** Callback when transcript update received */
  onTranscriptUpdate?: (transcripts: TranscriptionMessage[]) => void;
  /** Callback when notes update received */
  onNotesUpdate?: (notes: NoteItem[]) => void;
  /** Callback when chat update received */
  onChatUpdate?: (participant: ParticipantChatMessage[], officer: OfficerChatMessage[]) => void;
  /** Callback when AI analysis update received */
  onAiAnalysisUpdate?: (analysis: unknown) => void;
}

interface RealtimeState {
  isConnected: boolean;
  lastEventTime: number | null;
  error: Error | null;
  eventsReceived: number;
}

interface DbNoteItem {
  id: string;
  content: string;
  created_at_ms: number;
  updated_at_ms: number;
}

interface DbChatMessage {
  id: string;
  type: 'officer' | 'participant';
  sender_name: string;
  sender_identity: string;
  message: string;
  timestamp_ms: number;
  is_officer?: boolean;
  tps_results?: unknown;
}

/**
 * Convert DB note format to Redux format
 */
function dbNoteToRedux(item: DbNoteItem): NoteItem {
  return {
    id: item.id,
    content: item.content,
    createdAt: item.created_at_ms,
    updatedAt: item.updated_at_ms,
  };
}

/**
 * Convert DB chat format to Redux formats
 */
function dbChatToParticipant(item: DbChatMessage): ParticipantChatMessage {
  return {
    id: item.id,
    message: item.message,
    timestamp: item.timestamp_ms,
    from: {
      identity: item.sender_identity,
      name: item.sender_name,
      // By the sender's server-minted identity, never the browser-written flag (Q-153 r8, audit B30).
      isOfficer: isTrustedOfficerIdentity(item.sender_identity),
    },
  };
}

function dbChatToOfficer(item: DbChatMessage): OfficerChatMessage {
  return {
    id: item.id,
    message: item.message,
    timestamp: item.timestamp_ms,
    from: {
      identity: item.sender_identity,
      name: item.sender_name,
    },
    tpsResults: item.tps_results as OfficerChatMessage['tpsResults'],
  };
}

/**
 * Hook to subscribe to Supabase Realtime changes for a call
 */
export function useSupabaseRealtime(options: UseSupabaseRealtimeOptions): RealtimeState {
  const {
    callId,
    dbCallId,
    enabled = true,
    skipTranscripts = false,
    onTranscriptUpdate,
    onNotesUpdate,
    onChatUpdate,
    onAiAnalysisUpdate,
  } = options;

  const dispatch = useDispatch();
  const supabase = createClient();
  const normalizedStorageEnabled = isTranscriptNormalizedStorageEnabled();
  const [transcriptRealtimeMode, setTranscriptRealtimeMode] = useState<
    TranscriptRealtimeMode | null
  >(normalizedStorageEnabled ? null : 'legacy');

  const channelRef = useRef<RealtimeChannel | null>(null);
  const mountedRef = useRef(true);

  const [state, setState] = useState<RealtimeState>({
    isConnected: false,
    lastEventTime: null,
    error: null,
    eventsReceived: 0,
  });

  useEffect(() => {
    let active = true;
    if (!dbCallId) {
      setTranscriptRealtimeMode(normalizedStorageEnabled ? null : 'legacy');
      return () => { active = false; };
    }
    setTranscriptRealtimeMode(normalizedStorageEnabled ? null : 'legacy');
    void resolveTranscriptRealtimeMode(
      supabase,
      dbCallId,
      normalizedStorageEnabled,
    ).then((mode) => {
      if (active) setTranscriptRealtimeMode(mode);
    }).catch((error) => {
      logger.warn('useSupabaseRealtime', 'Transcript authority lookup failed; using dual Realtime bindings', {
        error,
        dbCallId,
      });
      if (active) setTranscriptRealtimeMode('dual');
    });
    return () => { active = false; };
  }, [dbCallId, normalizedStorageEnabled, supabase]);

  /**
   * Handle one normalized transcript segment INSERT/UPDATE.
   */
  const handleTranscriptChange = useCallback((
    payload: RealtimePostgresChangesPayload<TranscriptSegmentRow>
  ) => {
    if (!mountedRef.current) return;

    // When this device is actively receiving transcripts from LiveKit and writing
    // them via useTranscriptSync, skip Realtime transcript events entirely.
    if (skipTranscripts) {
      logger.debug('useSupabaseRealtime', 'Skipping transcript echo (active writer)', {
        eventType: payload.eventType,
      });
      return;
    }

    logger.debug('useSupabaseRealtime', 'Transcript change received', {
      eventType: payload.eventType,
    });

    const transcript = transcriptSegmentRowToMessage(
      payload.new as TranscriptSegmentRow | undefined,
    );
    if (transcript) {
      if (onTranscriptUpdate) {
        onTranscriptUpdate([transcript]);
      } else {
        dispatch(reconcilePersistedTranscripts([transcript]));
      }
    }

    setState(prev => ({
      ...prev,
      lastEventTime: Date.now(),
      eventsReceived: prev.eventsReceived + 1,
    }));
  }, [dispatch, onTranscriptUpdate, skipTranscripts]);

  /** Handle the whole-array legacy projection while the cutover gate is off. */
  const handleLegacyTranscriptChange = useCallback((
    payload: RealtimePostgresChangesPayload<LegacyTranscriptRealtimeRow>,
  ) => {
    if (!mountedRef.current || skipTranscripts) return;
    const content = (payload.new as LegacyTranscriptRealtimeRow | undefined)?.content;
    if (!Array.isArray(content)) return;

    const transcripts = content.map((item, index) => fromPersistedTranscript(item, index));
    if (onTranscriptUpdate) {
      onTranscriptUpdate(transcripts);
    } else {
      dispatch(mergeTranscripts(transcripts));
    }
    setState(prev => ({
      ...prev,
      lastEventTime: Date.now(),
      eventsReceived: prev.eventsReceived + 1,
    }));
  }, [dispatch, onTranscriptUpdate, skipTranscripts]);

  /**
   * Handle notes table changes
   */
  const handleNotesChange = useCallback((
    payload: RealtimePostgresChangesPayload<{ content: DbNoteItem[] }>
  ) => {
    if (!mountedRef.current) return;

    logger.debug('useSupabaseRealtime', 'Notes change received', {
      eventType: payload.eventType,
    });

    const newRecord = payload.new as { content?: DbNoteItem[] } | undefined;
    if (newRecord?.content) {
      const notes = newRecord.content.map(dbNoteToRedux);

      if (onNotesUpdate) {
        onNotesUpdate(notes);
      } else {
        dispatch(setNotes(notes));
      }
    }

    setState(prev => ({
      ...prev,
      lastEventTime: Date.now(),
      eventsReceived: prev.eventsReceived + 1,
    }));
  }, [dispatch, onNotesUpdate]);

  /**
   * Handle chat_messages table changes
   */
  const handleChatChange = useCallback((
    payload: RealtimePostgresChangesPayload<{ content: DbChatMessage[] }>
  ) => {
    if (!mountedRef.current) return;

    logger.debug('useSupabaseRealtime', 'Chat change received', {
      eventType: payload.eventType,
    });

    const newRecord = payload.new as { content?: DbChatMessage[] } | undefined;
    if (newRecord?.content) {
      const participantMsgs = newRecord.content
        .filter(m => m.type === 'participant')
        .map(dbChatToParticipant);
      const officerMsgs = dropUpstreamErrorMessages(newRecord.content
        .filter(m => m.type === 'officer')
        .map(dbChatToOfficer));

      if (onChatUpdate) {
        onChatUpdate(participantMsgs, officerMsgs);
      } else {
        for (const msg of participantMsgs) {
          dispatch(addParticipantChatMessage(msg));
        }
        for (const msg of officerMsgs) {
          dispatch(addOfficerChatMessage(msg));
        }
      }
    }

    setState(prev => ({
      ...prev,
      lastEventTime: Date.now(),
      eventsReceived: prev.eventsReceived + 1,
    }));
  }, [dispatch, onChatUpdate]);

  /**
   * Handle ai_analysis table changes
   */
  const handleAiAnalysisChange = useCallback((
    payload: RealtimePostgresChangesPayload<{ full_state: unknown }>
  ) => {
    if (!mountedRef.current) return;

    logger.debug('useSupabaseRealtime', 'AI analysis change received', {
      eventType: payload.eventType,
    });

    const newRecord = payload.new as { full_state?: unknown } | undefined;
    if (newRecord?.full_state && onAiAnalysisUpdate) {
      onAiAnalysisUpdate(newRecord.full_state);
    }

    setState(prev => ({
      ...prev,
      lastEventTime: Date.now(),
      eventsReceived: prev.eventsReceived + 1,
    }));
  }, [onAiAnalysisUpdate]);

  /**
   * Set up realtime subscription
   */
  useEffect(() => {
    if (!enabled || !dbCallId) {
      return;
    }
    let active = true;

    // Create channel for this call
    const channelName = `call:${callId}`;
    const channel = supabase.channel(channelName);

    // Central registrar selects normalized `table: 'transcript_segments'` or
    // the legacy whole-array binding from the reversible storage gate.
    if (!skipTranscripts && transcriptRealtimeMode) {
      registerTranscriptRealtimeSubscriptions({
        channel,
        dbCallId,
        mode: transcriptRealtimeMode,
        onNormalized: handleTranscriptChange,
        onLegacy: (payload) => {
          const row = payload.new as LegacyTranscriptRealtimeRow | undefined;
          const authorityMode = legacyEventAuthorityMode(
            row,
          );
          if (authorityMode && authorityMode !== transcriptRealtimeMode) {
            setTranscriptRealtimeMode(authorityMode);
          }
          if (authorityMode === 'normalized') return;
          handleLegacyTranscriptChange(payload);
        },
      });
    }

    // Subscribe to notes table changes
    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'notes',
        filter: `call_id=eq.${dbCallId}`,
      },
      handleNotesChange as (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
    );

    // Subscribe to chat_messages table changes
    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'chat_messages',
        filter: `call_id=eq.${dbCallId}`,
      },
      handleChatChange as (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
    );

    // Subscribe to ai_analysis table changes
    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'ai_analysis',
        filter: `call_id=eq.${dbCallId}`,
      },
      handleAiAnalysisChange as (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void
    );

    // Handle subscription status
    channel.subscribe((status) => {
      logger.debug('useSupabaseRealtime', 'Subscription status changed', { status, callId });

      if (status === 'SUBSCRIBED') {
        setState(prev => ({ ...prev, isConnected: true, error: null }));
        if (
          transcriptRealtimeMode !== 'legacy'
          && !skipTranscripts
        ) {
          void loadTranscriptProjection(supabase, dbCallId, {
            normalizedStorageEnabled: true,
          }).then((projection) => {
            if (!active) return;
            const transcripts = projection.transcripts.map(
              (item, index) => fromPersistedTranscript(item, index),
            );
            if (onTranscriptUpdate) onTranscriptUpdate(transcripts);
            else dispatch(reconcilePersistedTranscripts(transcripts));
          }).catch((error) => {
            logger.warn('useSupabaseRealtime', 'Normalized Realtime catch-up failed', {
              error,
              dbCallId,
            });
            if (active) setState(prev => ({ ...prev, error: error as Error }));
          });
        }
        if (transcriptRealtimeMode !== 'normalized') {
          void resolveTranscriptRealtimeMode(
            supabase,
            dbCallId,
            normalizedStorageEnabled,
          ).then((mode) => {
            if (active && mode !== transcriptRealtimeMode) {
              setTranscriptRealtimeMode(mode);
            }
          }).catch((error) => {
            logger.warn('useSupabaseRealtime', 'Post-subscribe transcript authority recheck failed', {
              error,
              dbCallId,
            });
          });
        }
      } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR') {
        setState(prev => ({
          ...prev,
          isConnected: false,
          error: new Error(`Channel ${status}`),
        }));
      }
    });

    channelRef.current = channel;

    logger.debug('useSupabaseRealtime', 'Subscribed to call channel', { callId, dbCallId });

    // Cleanup
    return () => {
      active = false;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
        logger.debug('useSupabaseRealtime', 'Unsubscribed from call channel', { callId });
      }
    };
  }, [
    enabled,
    callId,
    dbCallId,
    supabase,
    handleTranscriptChange,
    handleLegacyTranscriptChange,
    normalizedStorageEnabled,
    transcriptRealtimeMode,
    skipTranscripts,
    handleNotesChange,
    handleChatChange,
    handleAiAnalysisChange,
    dispatch,
    onTranscriptUpdate,
  ]);

  // Track mounted state
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return state;
}

/**
 * Hook to broadcast a message to other devices on the same call
 * Uses Supabase Realtime broadcast (ephemeral, no DB write)
 */
export function useRealtimeBroadcast(callId: string): {
  broadcast: (event: string, payload: unknown) => void;
  isConnected: boolean;
} {
  const supabase = createClient();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const channel = supabase.channel(`broadcast:${callId}`);

    channel.subscribe((status) => {
      setIsConnected(status === 'SUBSCRIBED');
    });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [callId, supabase]);

  const broadcast = useCallback((event: string, payload: unknown) => {
    if (channelRef.current) {
      channelRef.current.send({
        type: 'broadcast',
        event,
        payload,
      });
    }
  }, []);

  return { broadcast, isConnected };
}

export default useSupabaseRealtime;
